import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import type {
  DirectoryUser,
  MatterRole,
  PhaseActionDefinition,
  PhaseActionTaskTemplate,
} from "@/lib/types";
import { getDirectoryUsersByIdsOptions } from "@/lib/data/react-query/firm/firm-user";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Check,
  GripVertical,
  Info,
  ListTodo,
  Plus,
  Settings2,
  Trash2,
  UserRound,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import type { ProjectPhaseDraft } from "./PhaseProjectBuilder";
import {
  getSampleActionStatus,
  type SamplePhaseStatus,
} from "./phase-project-sample-data";
import { taskPersonLabel } from "./phase-task-presentation";

export function ActionSettingsMenu({
  actionId,
  title,
  phase,
  claimTypes,
  actionTaskTemplates,
  onEditActionTasks,
  onClaimTypesChange,
  onRemove,
}: {
  actionId: string;
  title: string;
  phase: ProjectPhaseDraft;
  claimTypes: { id: string; label: string }[];
  actionTaskTemplates?: Record<string, PhaseActionTaskTemplate[]>;
  onEditActionTasks?: (actionId: string) => void;
  onClaimTypesChange: (actionId: string, claimTypeIds: string[]) => void;
  onRemove: (actionId: string) => void;
}) {
  const selectedClaimTypes = phase.actionClaimTypeIds[actionId] ?? [];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label={`Settings for ${title}`}
        >
          <Settings2 className="size-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-48">
        {onEditActionTasks ? (
          <DropdownMenuItem
            onSelect={() => {
              onEditActionTasks(actionId);
            }}
          >
            {actionTaskTemplates?.[actionId]?.length
              ? "Edit automatic task"
              : "Create automatic task"}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Claim types</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
            <DropdownMenuItem
              onSelect={() => {
                onClaimTypesChange(actionId, []);
              }}
            >
              {selectedClaimTypes.length === 0 ? (
                <Check className="size-4" aria-hidden="true" />
              ) : (
                <span className="size-4" />
              )}
              All phase claim types
            </DropdownMenuItem>
            {claimTypes.length > 0 ? <DropdownMenuSeparator /> : null}
            {claimTypes.map((claimType) => (
              <DropdownMenuCheckboxItem
                key={claimType.id}
                checked={selectedClaimTypes.includes(claimType.id)}
                onSelect={(event) => {
                  event.preventDefault();
                }}
                onCheckedChange={() => {
                  onClaimTypesChange(
                    actionId,
                    selectedClaimTypes.length === 0
                      ? [claimType.id]
                      : selectedClaimTypes.includes(claimType.id)
                        ? selectedClaimTypes.filter((id) => id !== claimType.id)
                        : [...selectedClaimTypes, claimType.id],
                  );
                }}
              >
                {claimType.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onSelect={() => {
            onRemove(actionId);
          }}
        >
          <Trash2 className="size-4" aria-hidden="true" />
          Remove action
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ActionCard({
  actionId,
  action,
  phase,
  claimTypes,
  readOnly,
  showSampleData,
  sampleActionStatus,
  actionTaskTemplates,
  roles,
  users,
  onEditActionTasks,
  onClaimTypesChange,
  onRemove,
}: {
  actionId: string;
  action?: PhaseActionDefinition;
  phase: ProjectPhaseDraft;
  claimTypes: { id: string; label: string }[];
  readOnly: boolean;
  showSampleData: boolean;
  sampleActionStatus: "Complete" | "In progress" | "Incomplete";
  actionTaskTemplates?: Record<string, PhaseActionTaskTemplate[]>;
  roles: MatterRole[];
  users: DirectoryUser[];
  onEditActionTasks?: (actionId: string) => void;
  onClaimTypesChange: (actionId: string, claimTypeIds: string[]) => void;
  onRemove: (actionId: string) => void;
}) {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: actionId, disabled: readOnly });
  const title = action?.title ?? "Unavailable Action";
  const automaticTask = actionTaskTemplates?.[actionId]?.[0];
  const assignee = automaticTask
    ? taskPersonLabel(automaticTask, "assignee", roles, users)
    : null;
  const selectedClaimTypeIds = phase.actionClaimTypeIds[actionId] ?? [];
  const claimTypeLabel =
    selectedClaimTypeIds.length === 0
      ? "All phase claim types"
      : claimTypes
          .filter((claimType) => selectedClaimTypeIds.includes(claimType.id))
          .map((claimType) => claimType.label)
          .join(", ");
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? "relative z-10 opacity-60" : undefined}
    >
      <section className="bg-card flex min-h-44 min-w-0 flex-col justify-between gap-3 rounded-2xl border p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex min-w-0 items-start gap-2">
              <p className="text-sm font-medium break-words">{title}</p>
              {action?.description ? (
                <TooltipWrapper message={action.description}>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground shrink-0"
                    aria-label={`Completion rule for ${title}`}
                  >
                    <Info className="size-4" aria-hidden="true" />
                  </button>
                </TooltipWrapper>
              ) : null}
            </div>
            <p
              className="text-muted-foreground truncate text-xs"
              title={claimTypeLabel}
            >
              {claimTypeLabel}
            </p>
          </div>
          {!readOnly ? (
            <div className="-mt-1 -mr-1 flex shrink-0 items-center">
              <Button
                ref={setActivatorNodeRef}
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 cursor-grab active:cursor-grabbing"
                aria-label={`Drag to reorder ${title}`}
                {...attributes}
                {...listeners}
              >
                <GripVertical className="size-4" aria-hidden="true" />
              </Button>
              <ActionSettingsMenu
                actionId={actionId}
                title={title}
                phase={phase}
                claimTypes={claimTypes}
                actionTaskTemplates={actionTaskTemplates}
                onEditActionTasks={onEditActionTasks}
                onClaimTypesChange={onClaimTypesChange}
                onRemove={onRemove}
              />
            </div>
          ) : null}
        </div>
        <div className="space-y-3">
          {automaticTask ? (
            <div className="border-border/70 min-w-0 border-t pt-3">
              <div className="flex min-w-0 items-start gap-2">
                <ListTodo
                  className="text-muted-foreground mt-0.5 size-4 shrink-0"
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-muted-foreground text-xs">
                    Automatic task
                  </p>
                  <p
                    className="truncate text-sm font-medium"
                    title={automaticTask.title}
                  >
                    {automaticTask.title}
                  </p>
                  <p
                    className="text-muted-foreground flex min-w-0 items-center gap-1 text-xs"
                    title={`Assignee: ${assignee}`}
                  >
                    <UserRound
                      className="size-3.5 shrink-0"
                      aria-hidden="true"
                    />
                    <span className="truncate">{assignee}</span>
                  </p>
                </div>
                {onEditActionTasks ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="-mt-1 shrink-0"
                    onClick={() => {
                      onEditActionTasks(actionId);
                    }}
                  >
                    {readOnly ? "View" : "Edit"}
                  </Button>
                ) : null}
              </div>
            </div>
          ) : !readOnly && onEditActionTasks ? (
            <div className="border-border/70 flex items-center justify-between gap-2 border-t pt-3">
              <span className="text-muted-foreground text-xs">
                No automatic task
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  onEditActionTasks(actionId);
                }}
              >
                <Plus className="size-4" aria-hidden="true" />
                Add task
              </Button>
            </div>
          ) : null}
          {showSampleData ? (
            <Badge
              variant={
                sampleActionStatus === "Complete"
                  ? "success"
                  : sampleActionStatus === "In progress"
                    ? "warning"
                    : "outline"
              }
              className={`w-fit font-normal ${sampleActionStatus === "Incomplete" ? "text-muted-foreground" : ""}`}
            >
              {sampleActionStatus}
            </Badge>
          ) : null}
        </div>
      </section>
    </div>
  );
}

export function PhaseProjectActionCards({
  phase,
  actionDefinitions,
  claimTypes,
  readOnly,
  showSampleData,
  samplePhaseStatus,
  actionTaskTemplates,
  onEditActionTasks,
  onClaimTypesChange,
  onRemove,
  onReorder,
}: {
  phase: ProjectPhaseDraft;
  actionDefinitions: PhaseActionDefinition[];
  claimTypes: { id: string; label: string }[];
  readOnly: boolean;
  showSampleData: boolean;
  samplePhaseStatus: SamplePhaseStatus;
  actionTaskTemplates?: Record<string, PhaseActionTaskTemplate[]>;
  onEditActionTasks?: (actionId: string) => void;
  onClaimTypesChange: (actionId: string, claimTypeIds: string[]) => void;
  onRemove: (actionId: string) => void;
  onReorder: (event: DragEndEvent) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const phaseClaimTypes = claimTypes.filter(
    (claimType) =>
      phase.claimTypeIds.length === 0 ||
      phase.claimTypeIds.includes(claimType.id),
  );

  const groupedActionIds = new Set(
    phase.widgets.flatMap((widget) =>
      widget.type === "ActionGroup" ? widget.actionDefinitionIds : [],
    ),
  );
  const ungroupedActionIds = phase.actionDefinitionIds.filter(
    (id) => !groupedActionIds.has(id),
  );
  const templates = ungroupedActionIds.flatMap(
    (id) => actionTaskTemplates?.[id] ?? [],
  );
  const selectedUserIds = [
    ...new Set(templates.flatMap((template) => template.assigneeUserIds)),
  ];
  const settings = useQuery({
    ...retrieveMatterSettingsQueryOptions(),
    enabled: templates.length > 0,
  });
  const directory = useQuery(
    getDirectoryUsersByIdsOptions(selectedUserIds, selectedUserIds.length > 0),
  );
  const roles = settings.data?.data.roles ?? [];
  const users = directory.data ?? [];

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onReorder}
    >
      <SortableContext
        items={ungroupedActionIds}
        strategy={rectSortingStrategy}
      >
        <div className="grid min-w-0 items-stretch gap-4 @min-[760px]:grid-cols-2">
          {ungroupedActionIds.map((actionId, index) => (
            <ActionCard
              key={actionId}
              actionId={actionId}
              action={actionDefinitions.find((item) => item.id === actionId)}
              phase={phase}
              claimTypes={phaseClaimTypes}
              readOnly={readOnly}
              showSampleData={showSampleData}
              sampleActionStatus={getSampleActionStatus(
                samplePhaseStatus,
                index,
                ungroupedActionIds.length,
              )}
              actionTaskTemplates={actionTaskTemplates}
              roles={roles}
              users={users}
              onEditActionTasks={onEditActionTasks}
              onClaimTypesChange={onClaimTypesChange}
              onRemove={onRemove}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
